import { Nullable } from "@/flokit/types/nullable";
import { useVirtualizer } from "@tanstack/react-virtual";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import Select, {
  type GroupBase,
  type MenuListProps,
  type MenuPlacement,
} from "react-select";

import { omit } from "lodash";
import CreatableSelect from "react-select/creatable";
import {
  type SelectComponents,
  SelectComponentsConfig,
} from "react-select/dist/declarations/src/components";
import { MODAL_SHELL_CONTENT_SCROLL_TOP_CSS_VARIABLE } from "../../modals/modal-shell";
import * as CustomComponents from "./custom-components";

const charcoal = "rgba(33, 33, 33, 1)";
const friendlyEskimo = "rgba(247, 249, 253, 1)";
const dublinGray = "rgba(221, 221, 221, 1)";
const magenta = "#ba2563";
const navy = "#286da8";
const macbook = "rgba(171, 171, 171, 1)";
const error = "rgb(219, 95, 83)";
const COLOR_VARIANTS = {
  magenta,
  navy,
};

export interface SelectOption {
  label: string;
  value: string;
}

type SelectValue<IsMulti extends boolean> = IsMulti extends true
  ? readonly SelectOption[]
  : Nullable<SelectOption>;

interface AccessibilityProps {
  ariaActiveDescendant?: [string, (newValue: string) => void];
  isRequired?: boolean;
  ariaLabelledBy?: string;
  id?: string;
  ariaDescribedBy?: string;
}

const SelectAccessibilityContext = createContext<AccessibilityProps>({
  ariaActiveDescendant: ["", () => {}],
  isRequired: false,
  ariaLabelledBy: "",
  id: "",
  ariaDescribedBy: "",
});
export function _useSelectAriaActiveDescendantContext() {
  return useContext(SelectAccessibilityContext);
}

export type _BaseSelectProps<IsMulti extends boolean> = {
  isVirtualized?: boolean;
  value: SelectValue<IsMulti>;
  onChange: (newValue: SelectValue<IsMulti>) => void;
  options: SelectOption[];
  isMulti: IsMulti;

  placeholder?: string;
  isRequired?: boolean;
  isDisabled?: boolean;
  onFocus?: (e: React.FocusEvent<HTMLInputElement, Element>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement, Element>) => void;

  noOptionsMessage?: string;
  isClearable?: boolean;
  isSearchable?: boolean;
  isOptionDisabled?: (option: SelectOption) => boolean;
  onCreateNewItem?: (itemName: string) => void;
  isValidNewOption?: (inputValue: string) => boolean;

  isLoading?: boolean;
  hasError?: boolean;

  color?: "magenta" | "navy";
  menuPlacement?: MenuPlacement;
  menuPortalTarget?: HTMLElement;
  UNSAFE_Components?: Partial<
    SelectComponents<SelectOption, boolean, GroupBase<SelectOption>>
  >;
};

const VirtualizedMenuList = (
  props: MenuListProps<SelectOption, boolean> & {
    noOptionsMessage?: string;
  },
) => {
  const { children, maxHeight } = props;

  const scrollRef = useRef<HTMLDivElement>(null);

  const { ariaActiveDescendant: ariaActiveDescendantPair } =
    _useSelectAriaActiveDescendantContext();
  const [ariaActiveDescendant] = ariaActiveDescendantPair;

  const optionChildren = React.Children.toArray(children).filter(
    (child): child is React.ReactElement<React.HTMLAttributes<HTMLElement>> =>
      React.isValidElement(child),
  );

  const itemCount = optionChildren.length;

  const rowVirtualizer = useVirtualizer({
    count: itemCount,
    getScrollElement: () => scrollRef.current,
    estimateSize: (index) => 40,
    overscan: 5,
  });

  useEffect(() => {
    if (!rowVirtualizer || !ariaActiveDescendant) return;

    const indexStr = ariaActiveDescendant.match(/-(\d+)$/)?.[1];
    if (indexStr) {
      const index = Number.parseInt(indexStr, 10);

      if (!Number.isNaN(index) && index >= 0 && index < itemCount) {
        setTimeout(() => {
          rowVirtualizer.scrollToIndex(index, { align: "auto" });
        }, 0);
      }
    }
  }, [rowVirtualizer, ariaActiveDescendant, itemCount]);

  const virtualItems = rowVirtualizer.getVirtualItems();

  if (!itemCount) {
    return (
      <div
        ref={scrollRef}
        style={{ maxHeight: `${maxHeight}px`, overflowY: "auto" }}
      >
        {props.noOptionsMessage}
      </div>
    );
  }

  return (
    <div
      ref={scrollRef}
      style={{
        maxHeight: `${maxHeight}px`,
        overflowY: "auto",
        position: "relative",
      }}
    >
      <div
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
          width: "100%",
          position: "relative",
        }}
      >
        {virtualItems.map((virtualItem) => {
          const optionElement = optionChildren[virtualItem.index];
          return (
            <div
              key={virtualItem.key.toString()}
              ref={rowVirtualizer.measureElement}
              data-index={virtualItem.index}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                transform: `translateY(${virtualItem.start}px)`,
                whiteSpace: "normal",
                wordBreak: "break-word",
                overflowWrap: "break-word",
              }}
            >
              {optionElement &&
                React.cloneElement(optionElement, {
                  "aria-posinset": virtualItem.index + 1,
                  "aria-setsize": itemCount,
                })}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const _BaseSelect = <IsMulti extends boolean>(
  props: _BaseSelectProps<IsMulti> & {
    id?: string;
    "aria-labelledby"?: string;
    "aria-describedby"?: string;
  },
) => {
  const color = props.hasError
    ? error
    : COLOR_VARIANTS[props.color ?? "magenta"];

  const Component = props.onCreateNewItem ? CreatableSelect : Select;

  const [ariaActiveDescendant, setAriaActiveDescendant] = useState("");

  let customComponents: SelectComponentsConfig<
    SelectOption,
    boolean,
    GroupBase<SelectOption>
  > = {
    Input: CustomComponents.InputWithEnterKeyFix,
    Option: CustomComponents.AccessibleOption,
    MenuList: VirtualizedMenuList,
    ...props.UNSAFE_Components,
  };

  if (!props.isVirtualized) {
    customComponents = omit(customComponents, "MenuList");
  }

  return (
    <SelectAccessibilityContext.Provider
      value={{
        ariaActiveDescendant: [ariaActiveDescendant, setAriaActiveDescendant],
        isRequired: props.isRequired ?? false,
        id: props.id,
        ariaLabelledBy: props["aria-labelledby"],
        ariaDescribedBy: props["aria-describedby"],
      }}
    >
      <Component
        value={props.value}
        onChange={props.onChange}
        options={props.options}
        isMulti={props.isMulti}
        placeholder={props.placeholder}
        isDisabled={props.isDisabled}
        noOptionsMessage={() => props.noOptionsMessage}
        isClearable={props.isClearable}
        isSearchable={props.isSearchable}
        isOptionDisabled={props.isOptionDisabled}
        onCreateOption={props.onCreateNewItem}
        isLoading={props.isLoading}
        onBlur={props.onBlur}
        onFocus={props.onFocus}
        menuPlacement={props.menuPlacement}
        menuPortalTarget={props.menuPortalTarget}
        menuPosition={"fixed"}
        isValidNewOption={props.isValidNewOption}
        components={customComponents}
        styles={{
          menuPortal: (base) => {
            if (!props.menuPortalTarget) return {};

            // Position popover relative to the wrapper div so that it is positioned under the input. In modals, we want it to be "above" everything, e.g. it should go over the modal footer and outside the modal.
            // Essentially it lives at a much higher z-index than the input so that it goes outside the modal, but we position its (x,y) coordinates so that it looks attached to the input.
            // To maintain this illusion when the modal content is scrollable, we need to account for the 'scrollTop' attribute of the input. Otherwise, when the user scrolls the content, the menu will be "attached" to the input's original (x,y) position on the screen, not its new, scrolled position on the screen.
            const el = props.menuPortalTarget;
            return {
              ...base,
              left: 0,
              top: 0,
              transform: `translate(${el.offsetLeft}px, calc(${
                el.offsetTop + el.clientHeight
              }px - var(${MODAL_SHELL_CONTENT_SCROLL_TOP_CSS_VARIABLE}, 0px)))`,
            };
          },
          menu: (base) => {
            return {
              ...base,
              // Higher than ::before on radio button
              zIndex: 20,
            };
          },
          container: (base) => {
            return { ...base, width: "100%", maxWidth: "100%" };
          },
          placeholder: (base) => {
            return { ...base, color: macbook, fontSize: "0.875rem" };
          },
          control: (base, state) => {
            return {
              ...base,
              "&:hover": {
                borderColor: dublinGray,
              },
              height: "100%",
              borderColor:
                state.isFocused || props.hasError ? color : dublinGray,
              boxShadow: "none",
              outline:
                state.isFocused || props.hasError
                  ? `1px solid ${color}`
                  : "none",
            };
          },
          singleValue: (base) => {
            return {
              ...base,
              fontSize: "0.875rem",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverFlow: "ellipsis",
            };
          },
          multiValueLabel: (base) => {
            return {
              ...base,
              color: charcoal,
              fontWeight: 500,
            };
          },
          multiValue: (base) => {
            return {
              ...base,
              backgroundColor: friendlyEskimo,
            };
          },
          noOptionsMessage: (base) => {
            return {
              ...base,
              color: macbook,
              fontSize: "0.875rem",
            };
          },
          option: (base, state) => {
            const common = { cursor: "pointer" };
            if (state.isDisabled) {
              return {
                ...common,
                ...base,
                fontSize: "0.875rem",
              };
            }
            return {
              ...base,
              ...common,
              "&:active": { backgroundColor: friendlyEskimo },
              "&:hover": {
                borderColor: dublinGray,
              },
              fontSize: "0.875rem",
              backgroundColor: state.isFocused ? friendlyEskimo : "transparent",
              color: state.isFocused || state.isSelected ? navy : charcoal,
              fontWeight: 500,
            };
          },
        }}
      />
    </SelectAccessibilityContext.Provider>
  );
};
